import { motion } from "framer-motion";
import {
  MousePointerClick,
  Zap,
  Database,
  LayoutTemplate,
  Palette,
  Figma,
  Bot,
  Smartphone,
  BrainCircuit,
  KeyRound,
  Layers,
  Rocket,
} from "lucide-react";
import { Chapter, SectionTitle, SectionSub, LovableLogo, EmergentLogo, EASE } from "./shared";

const LOVABLE_STRENGTHS = [
  { icon: MousePointerClick, title: "Visual Editor", desc: "Point, click and restyle any element without writing a prompt." },
  { icon: Zap, title: "Instant Previews", desc: "See every change live in the browser within seconds." },
  { icon: Database, title: "Supabase + GitHub Sync", desc: "One-click backend wiring and two-way code sync." },
  { icon: LayoutTemplate, title: "Template Community", desc: "A huge gallery of remixable starters for any niche." },
  { icon: Palette, title: "Design-Grade Output", desc: "Generations that look hand-crafted, not AI-made." },
  { icon: Figma, title: "Figma-to-Code", desc: "Turn existing design files straight into working UI." },
];

const EMERGENT_STRENGTHS = [
  { icon: Bot, title: "Multi-Agent System", desc: "Specialized agents plan, build, review and fix together." },
  { icon: Smartphone, title: "Native iOS & Android", desc: "Ship real mobile apps via Expo, not just websites." },
  { icon: BrainCircuit, title: "1M Token Context", desc: "The agent remembers your entire codebase, always." },
  { icon: KeyRound, title: "Built-in AI Features", desc: "Add chat, vision, voice and image AI with one key." },
  { icon: Layers, title: "Full-Stack Autonomy", desc: "Frontend, backend, database and auth from one prompt." },
  { icon: Rocket, title: "Autonomous Testing & Deploy", desc: "Agents test the app themselves, then deploy it live." },
];

const StrengthCard = ({ icon: Icon, title, desc, accent }) => (
  <motion.div
    whileHover={{ y: -5 }}
    transition={{ type: "spring", stiffness: 260, damping: 20 }}
    className={`group rounded-2xl border border-white/10 bg-[#111111]/70 p-5 transition-[border-color,box-shadow] duration-300 ${
      accent === "lov"
        ? "hover:border-[#FF2D55]/40 hover:shadow-[0_12px_40px_-10px_rgba(255,45,85,0.3)]"
        : "hover:border-cyan-400/40 hover:shadow-[0_12px_40px_-10px_rgba(34,211,238,0.3)]"
    }`}
  >
    <span
      className={`flex h-10 w-10 items-center justify-center rounded-xl border ${
        accent === "lov"
          ? "border-[#FF2D55]/30 bg-[#FF2D55]/10"
          : "border-cyan-400/30 bg-cyan-400/10"
      }`}
    >
      <Icon className={`h-5 w-5 ${accent === "lov" ? "text-[#FF5C8A]" : "text-cyan-300"}`} />
    </span>
    <h4 className="font-display mt-4 text-base font-bold text-white">{title}</h4>
    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{desc}</p>
  </motion.div>
);

export default function FeatureShowdown() {
  return (
    <section id="features" className="relative py-11">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="04" label="Deep Feature Showdown" testId="features-chapter-badge" />
        <SectionTitle>
          Where each builder <span className="text-gradient-cyan">actually wins</span>
        </SectionTitle>
        <SectionSub>
          No tool is best at everything. These are the six standout strengths our testing
          confirmed for each platform.
        </SectionSub>

        <div className="mt-14 grid gap-8 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
            data-testid="showdown-lovable-panel"
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A]/80 p-7 backdrop-blur-xl sm:p-8"
          >
            <div className="pointer-events-none absolute -left-24 -top-24 h-56 w-56 rounded-full bg-[#FF2D55]/12 blur-[80px]" />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#FF2D55]/70 to-transparent" />
            <div className="relative mb-5 flex items-center gap-3">
              <LovableLogo size={38} />
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-white">
                  Lovable strengths
                </h3>
                <p className="font-mono2 mt-0.5 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  Design velocity
                </p>
              </div>
            </div>
            <div className="relative grid gap-4 sm:grid-cols-2">
              {LOVABLE_STRENGTHS.map((s) => (
                <StrengthCard key={s.title} {...s} accent="lov" />
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
            data-testid="showdown-emergent-panel"
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0A0A0A]/80 p-7 backdrop-blur-xl sm:p-8"
          >
            <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-cyan-400/12 blur-[80px]" />
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/70 to-transparent" />
            <div className="relative mb-5 flex items-center gap-3">
              <EmergentLogo size={38} />
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-white">
                  Emergent strengths
                </h3>
                <p className="font-mono2 mt-0.5 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                  Full-stack depth
                </p>
              </div>
            </div>
            <div className="relative grid gap-4 sm:grid-cols-2">
              {EMERGENT_STRENGTHS.map((s) => (
                <StrengthCard key={s.title} {...s} accent="emg" />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
